/**
 * Géographie du Live School (helpers PURS). Sert aux segments de classement
 * Région / Département / Pays et au contrôle de profil (`student-geo-control`).
 *
 * - `REGION_CODES` est le MIROIR exact de l'enum SQL `public.region_code`
 *   (migration 0155) — garder synchro (les deux listent les 18 régions FR :
 *   13 métropolitaines + 5 d'outre-mer).
 * - Tous les champs géo sont FACULTATIFS : `null` est géré partout.
 */

export const REGION_CODES = [
  'ile_de_france',
  'centre_val_de_loire',
  'bourgogne_franche_comte',
  'normandie',
  'hauts_de_france',
  'grand_est',
  'pays_de_la_loire',
  'bretagne',
  'nouvelle_aquitaine',
  'occitanie',
  'auvergne_rhone_alpes',
  'provence_alpes_cote_d_azur',
  'corse',
  'guadeloupe',
  'martinique',
  'guyane',
  'la_reunion',
  'mayotte',
] as const;

export type RegionCode = (typeof REGION_CODES)[number];

export const REGION_LABELS: Record<RegionCode, string> = {
  ile_de_france: 'Île-de-France',
  centre_val_de_loire: 'Centre-Val de Loire',
  bourgogne_franche_comte: 'Bourgogne-Franche-Comté',
  normandie: 'Normandie',
  hauts_de_france: 'Hauts-de-France',
  grand_est: 'Grand Est',
  pays_de_la_loire: 'Pays de la Loire',
  bretagne: 'Bretagne',
  nouvelle_aquitaine: 'Nouvelle-Aquitaine',
  occitanie: 'Occitanie',
  auvergne_rhone_alpes: 'Auvergne-Rhône-Alpes',
  provence_alpes_cote_d_azur: "Provence-Alpes-Côte d'Azur",
  corse: 'Corse',
  guadeloupe: 'Guadeloupe',
  martinique: 'Martinique',
  guyane: 'Guyane',
  la_reunion: 'La Réunion',
  mayotte: 'Mayotte',
};

/** Départements (codes INSEE) par région — source de vérité de la carte dépt → région. */
const REGION_DEPARTEMENTS: Record<RegionCode, readonly string[]> = {
  ile_de_france: ['75', '77', '78', '91', '92', '93', '94', '95'],
  centre_val_de_loire: ['18', '28', '36', '37', '41', '45'],
  bourgogne_franche_comte: ['21', '25', '39', '58', '70', '71', '89', '90'],
  normandie: ['14', '27', '50', '61', '76'],
  hauts_de_france: ['02', '59', '60', '62', '80'],
  grand_est: ['08', '10', '51', '52', '54', '55', '57', '67', '68', '88'],
  pays_de_la_loire: ['44', '49', '53', '72', '85'],
  bretagne: ['22', '29', '35', '56'],
  nouvelle_aquitaine: ['16', '17', '19', '23', '24', '33', '40', '47', '64', '79', '86', '87'],
  occitanie: ['09', '11', '12', '30', '31', '32', '34', '46', '48', '65', '66', '81', '82'],
  auvergne_rhone_alpes: ['01', '03', '07', '15', '26', '38', '42', '43', '63', '69', '73', '74'],
  provence_alpes_cote_d_azur: ['04', '05', '06', '13', '83', '84'],
  corse: ['2A', '2B'],
  guadeloupe: ['971'],
  martinique: ['972'],
  guyane: ['973'],
  la_reunion: ['974'],
  mayotte: ['976'],
};

const DEPARTEMENT_TO_REGION: Record<string, RegionCode> = (() => {
  const map: Record<string, RegionCode> = {};
  for (const code of REGION_CODES) {
    for (const dept of REGION_DEPARTEMENTS[code]) map[dept] = code;
  }
  return map;
})();

/**
 * Normalise un code département saisi : trim, majuscule (Corse 2A/2B), zéro de
 * tête pour les codes métropolitains à 1 chiffre (« 1 » → « 01 »). Renvoie
 * `null` si la valeur n'est pas un département valide.
 */
export function normalizeDepartement(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  let v = raw.trim().toUpperCase();
  if (v === '') return null;
  // 2A/2B (Corse).
  if (v === '2A' || v === '2B') return v;
  // Métropole à 1 chiffre → padding (« 1 » → « 01 », « 9 » → « 09 »).
  if (/^[1-9]$/.test(v)) v = `0${v}`;
  return isValidDepartement(v) ? v : null;
}

/** Vrai si le code (déjà normalisé) correspond à un département connu. */
export function isValidDepartement(code: string): boolean {
  return Object.prototype.hasOwnProperty.call(DEPARTEMENT_TO_REGION, code);
}

/** Région d'un département (pour pré-remplir la région depuis le département). `null` si inconnu. */
export function regionForDepartement(dept: string | null | undefined): RegionCode | null {
  const norm = normalizeDepartement(dept);
  if (norm == null) return null;
  return DEPARTEMENT_TO_REGION[norm] ?? null;
}

/** Liste ordonnée { value, label } des départements d'une région (pour un select). */
export function departementsForRegion(region: RegionCode): readonly string[] {
  return REGION_DEPARTEMENTS[region] ?? [];
}

// ── Pays (ISO-3166-1 alpha-2) — FR + voisins / francophonie courants ──────────

export const COUNTRY_CODES = [
  'FR',
  'BE',
  'CH',
  'LU',
  'MC',
  'CA',
  'MA',
  'DZ',
  'TN',
  'SN',
  'CI',
  'CM',
] as const;

export type CountryCode = (typeof COUNTRY_CODES)[number];

export const COUNTRY_LABELS: Record<CountryCode, string> = {
  FR: 'France',
  BE: 'Belgique',
  CH: 'Suisse',
  LU: 'Luxembourg',
  MC: 'Monaco',
  CA: 'Canada',
  MA: 'Maroc',
  DZ: 'Algérie',
  TN: 'Tunisie',
  SN: 'Sénégal',
  CI: "Côte d'Ivoire",
  CM: 'Cameroun',
};

export const DEFAULT_COUNTRY: CountryCode = 'FR';

/** Normalise un code pays (majuscule, validé). `null` si inconnu/vide. */
export function normalizeCountry(raw: string | null | undefined): CountryCode | null {
  if (raw == null) return null;
  const v = raw.trim().toUpperCase();
  return (COUNTRY_CODES as readonly string[]).includes(v) ? (v as CountryCode) : null;
}

/** Vrai si le code région appartient à l'enum. */
export function isValidRegion(code: string): code is RegionCode {
  return (REGION_CODES as readonly string[]).includes(code);
}
